import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type AppServicesWaldenGreenController from 'walden-fast/controllers/app/services/walden-green';
import type {
  AppServicesWaldenGreenOverviewModel,
  DateRangeOption,
} from 'walden-fast/routes/app/services/walden-green/overview';
import type FastApiService from 'walden-fast/services/fast-api';
import type ModalService from 'walden-fast/services/modal';
import type UserService from 'walden-fast/services/user';
import type { FilterValues } from 'walden-fast/types/filters';
import type { SortDirection } from 'walden-fast/types/pagination';
import { type ClientCompany } from 'walden-fast/types/user';

type Range = 'ytd' | 'mtd';
type PurchaseType = 'trees' | 'offset';

export default class AppServicesWaldenGreenOverviewController extends Controller {
  declare model: AppServicesWaldenGreenOverviewModel;

  @controller('app.services.walden-green')
  parent!: AppServicesWaldenGreenController;

  @service('fast-api') declare fastApiService: FastApiService;
  @service('intl') declare intlService: IntlService;
  @service('modal') declare modalService: ModalService;
  @service('user') declare userService: UserService;

  treeOffsetInformationModal = 'tree-information';
  treeOffsetPurchaseModal = 'tree-offset-purchase';
  treeOffsetPurchaseCompleteModal = 'tree-offset-purchase-complete-modal';

  // read the parent’s tracked property
  get numbersDataRange(): Range {
    return this.parent.numbersDataRange;
  }

  queryParams = [
    'dateRange',
    'clientCode',
    'purchaseHistorySortKey',
    'purchaseHistorySortDirection',
  ];

  @tracked dateRange: DateRangeOption = '1year';
  @tracked clientCode: string | undefined;
  @tracked purchaseHistorySortKey: string | undefined;
  @tracked purchaseHistorySortDirection: SortDirection | undefined;
  @tracked purchaseHistoryPage: number = 1;
  @tracked purchaseHistoryPageSize: number = 5;
  @tracked purchaseType: PurchaseType = 'trees';
  @tracked selectedPurchasePreset: number | undefined;
  @tracked manualPurchaseAmount: string | undefined;
  @tracked _showDisclaimer: boolean | undefined;

  get succesfulModel() {
    return this.model.lastSuccessful?.value;
  }

  get modelLoading() {
    return this.model.isRunning;
  }

  get powerBiFilters(): FilterValues | undefined {
    const now = new Date();
    const fromDate = new Date(now);

    switch (this.dateRange) {
      case '1month':
        fromDate.setMonth(now.getMonth() - 1);
        break;
      case '6months':
        fromDate.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        fromDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        fromDate.setFullYear(now.getFullYear() - 1);
    }

    return {
      fromMonth: String(fromDate.getMonth() + 1),
      fromYear: String(fromDate.getFullYear()),
      toMonth: String(now.getMonth() + 1),
      toYear: String(now.getFullYear()),
      clients: this.selectedClientCompany ? [this.selectedClientCompany] : [],
    };
  }

  get hasLevel2Data(): boolean {
    return !!this.succesfulModel?.level2Data;
  }

  get hasLevel3Data(): boolean {
    return !!this.succesfulModel?.level3Data;
  }

  get selectedClientCompany(): ClientCompany | undefined {
    return this.clientCompanies.find(
      (company) => company.id === this.clientCode,
    );
  }

  get emissionChartDataSetsConfig() {
    const emissionValues =
      this.succesfulModel?.emissions?.emissionDetails.map(
        ({ totalCO2Tonnes }) => totalCO2Tonnes,
      ) || [];

    return {
      ...this.calculateNiceMaxAndStep(emissionValues, 1.05),
      xAxis: {},
    };
  }

  get emissionOffsetChartDataSetsConfig() {
    if (!this.succesfulModel?.level2Data) {
      return {
        max: 50,
        stepSize: 5,
        xAxis: {},
      };
    }

    const allValues = [
      ...(this.succesfulModel?.emissions?.emissionDetails.map(
        ({ totalCO2Tonnes }) => totalCO2Tonnes,
      ) || []),
      ...(this.succesfulModel?.level2Data?.offsets.map(
        ({ tonnesCo2 }) => tonnesCo2,
      ) || []),
    ];

    return {
      ...this.calculateNiceMaxAndStep(allValues, 1.05),
      xAxis: {},
    };
  }

  get sortedEmissionDetails() {
    const details = this.succesfulModel?.emissions?.emissionDetails ?? [];
    return details
      .slice()
      .sort(
        (a, b) =>
          new Date(a.dateInIso8601).getTime() -
          new Date(b.dateInIso8601).getTime(),
      );
  }

  get emissionChartData() {
    return this.succesfulModel
      ? [
          {
            label: this.intlService.t(
              'pages.waldenGreen.emissions.chart.legendLabel.emissions',
            ),
            data: this.sortedEmissionDetails?.map((emission) => ({
              key: this.intlService.formatDate(emission.dateInIso8601),
              value: Number(emission.totalCO2Tonnes.toFixed(3)),
            })),
          },
        ]
      : [];
  }

  get emissionOffsetChartData() {
    if (!this.succesfulModel?.level2Data) {
      return [];
    }

    const sortedOffsets = this.succesfulModel.level2Data.offsets
      .slice()
      .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());

    return [
      {
        label: this.intlService.t(
          'pages.waldenGreen.emissions.chart.legendLabel.emissions',
        ),
        data: this.sortedEmissionDetails?.map((emission) => ({
          key: this.intlService.formatDate(emission.dateInIso8601),
          value: Number(emission.totalCO2Tonnes.toFixed(3)),
        })),
      },
      {
        label: this.intlService.t(
          'pages.waldenGreen.emissions.chart.legendLabel.offsets',
        ),
        data: sortedOffsets?.map((offset) => ({
          key: this.intlService.formatDate(offset.day),
          value: offset.tonnesCo2,
        })),
      },
    ];
  }

  get footprintCompensation() {
    if (!this.succesfulModel?.level2Data) {
      return 0;
    }

    return (
      this.co2coveredByTrees /
      this.succesfulModel?.level2Data.totalOffsetCo2Tonnes[
        this.numbersDataRange
      ]
    );
  }

  get co2coveredByTrees() {
    if (!this.succesfulModel?.level2Data) {
      return 0;
    }

    // todo: this 0.9 should be fetched from the backend somewhere
    return (
      this.succesfulModel?.level2Data.treesPlanted[this.numbersDataRange] * 0.9
    );
  }

  get offsetTreeData() {
    if (!this.succesfulModel?.level2Data) {
      return [];
    }

    return [
      {
        label: '',
        data: [
          {
            key: this.intlService.t(
              'pages.waldenGreen.offsetTreeData.includedTrees',
            ),
            value:
              this.succesfulModel?.level2Data.totalTreesIncluded[
                this.numbersDataRange
              ],
          },
          {
            key: this.intlService.t(
              'pages.waldenGreen.offsetTreeData.extraTrees',
            ),
            value:
              this.succesfulModel?.level2Data.totalTreesNonIncluded[
                this.numbersDataRange
              ],
          },
          {
            key: '', // Remaining chart space
            value: Math.max(
              0,
              this.succesfulModel?.level2Data.totalOffsetCo2Tonnes[
                this.numbersDataRange
              ] - this.co2coveredByTrees,
            ),
          },
        ],
      },
    ];
  }

  get clientCompanies() {
    return this.userService.user?.clientCompanies || [];
  }

  get purchasePresets() {
    return [
      {
        trees: 20,
        offset: 20 * this.oneTreeOffset,
      },
      {
        trees: 50,
        offset: 50 * this.oneTreeOffset,
      },
      {
        trees: 100,
        offset: 100 * this.oneTreeOffset,
      },
    ];
  }

  get maxPurchaseAmount() {
    return this.purchaseType === 'trees' ? 100 : 90;
  }

  get oneTreeOffset() {
    return 0.9;
  }

  get calculatedTrees() {
    let amount = 0;

    if (this.selectedPurchasePreset !== undefined) {
      amount = this.purchasePresets[this.selectedPurchasePreset]?.[
        this.purchaseType
      ] as number;
    } else if (
      this.manualPurchaseAmount !== undefined &&
      this.manualPurchaseAmount !== ''
    ) {
      amount = parseFloat(this.manualPurchaseAmount);
    }

    return Math.round(
      amount * (this.purchaseType === 'offset' ? 1.1111111111 : 1),
    );
  }

  get totalPrice() {
    if (!this.succesfulModel?.level3Data) {
      return 0;
    }

    return this.calculatedTrees * this.succesfulModel?.level3Data.treePrice;
  }

  get safeTreesPlanted() {
    return (
      this.succesfulModel?.level2Data?.treesPlanted?.[this.numbersDataRange] ||
      0
    );
  }

  get safeTonnesOfCo2() {
    return (
      this.succesfulModel?.level2Data?.tonnesOfCo2?.[this.numbersDataRange] || 0
    );
  }

  get safeWorkingHours() {
    return (
      this.succesfulModel?.level2Data?.workingHours?.[this.numbersDataRange] ||
      0
    );
  }

  get safeOffsetPurchases() {
    return this.succesfulModel?.level3Data?.offsetPurchases || [];
  }

  get safeOffsetPurchaseCount() {
    return this.succesfulModel?.level3Data?.offsetPurchaseCount || 0;
  }

  get safeTreePrice() {
    return this.succesfulModel?.level3Data?.treePrice || 0;
  }

  get safeTreePriceCurrency() {
    return this.succesfulModel?.level3Data?.treePriceCurrency || 'EUR';
  }

  get firstReportPage() {
    return this.succesfulModel?.greenReport?.pages?.[0];
  }

  get reportDisplayName() {
    return this.succesfulModel?.greenReport?.report?.displayName;
  }

  get showDisclaimer(): boolean {
    return this._showDisclaimer === undefined
      ? this.succesfulModel?.initialShowDisclaimer || false
      : this._showDisclaimer;
  }

  set showDisclaimer(value: boolean) {
    this._showDisclaimer = value;
  }

  get isPurchaseSuccess() {
    return this.purchase.last?.isSuccessful;
  }

  calculateNiceMaxAndStep(values: number[], paddingMultiplier: number = 1.05) {
    const max = Math.max(...values);

    if (max === 0) return { max: 1, stepSize: 0.2 };

    const paddedMax = max * paddingMultiplier;
    const magnitude = Math.pow(10, Math.floor(Math.log10(paddedMax)));
    const normalized = paddedMax / magnitude;

    let niceMax, stepSize;
    if (normalized <= 2) {
      niceMax = 2;
      stepSize = 0.5;
    } else if (normalized <= 4) {
      niceMax = 4;
      stepSize = 1;
    } else if (normalized <= 6) {
      niceMax = 6;
      stepSize = 1;
    } else if (normalized <= 8) {
      niceMax = 8;
      stepSize = 2;
    } else {
      niceMax = 10;
      stepSize = 2;
    }

    return {
      max: niceMax * magnitude,
      stepSize: stepSize * magnitude,
    };
  }

  sortDirectionForField = (field: string) => {
    return this.purchaseHistorySortKey === field
      ? this.purchaseHistorySortDirection
      : 'none';
  };

  @action
  setNumbersDataRange(range: Range) {
    this.parent.numbersDataRange = range;
  }

  @action
  sort(key: string, direction: SortDirection) {
    this.purchaseHistorySortKey = key;
    this.purchaseHistorySortDirection = direction;
  }

  @action
  openTreePurchaseModal() {
    this.modalService.open(this.treeOffsetPurchaseModal);
  }

  @action
  openTreePlantedModal() {
    this.modalService.open(this.treeOffsetInformationModal);
  }

  @action
  closeTreePurchaseModal() {
    this.modalService.close(this.treeOffsetPurchaseModal);
  }

  @action
  closeTreePurchaseCompleteModal() {
    // Reset state
    this.purchaseType = 'trees';
    this.selectedPurchasePreset = undefined;
    this.manualPurchaseAmount = undefined;
    this.modalService.close(this.treeOffsetPurchaseCompleteModal);
  }

  @action
  selectPurchaseType(type: PurchaseType) {
    this.purchaseType = type;
  }

  @action
  selectPurchasePreset(preset: number) {
    this.selectedPurchasePreset = preset;
    this.manualPurchaseAmount = undefined;
  }

  @action
  updateManualPurchaseAmount(value: string) {
    this.manualPurchaseAmount = value;
    this.selectedPurchasePreset = undefined;
  }

  @action
  onHideDisclaimer() {
    this.showDisclaimer = false;
    window.localStorage.setItem('hideDisclaimer', 'true');
  }

  @action
  performPurchase() {
    this.purchase
      .perform()
      .catch(() => {
        console.error('purchase failed');
      })
      .finally(() => {
        this.closeTreePurchaseModal();
        this.modalService.open(this.treeOffsetPurchaseCompleteModal);
      });
  }

  purchase = task(async () => {
    const result = await this.fastApiService.post(
      '/walden-green/offset_purchases',
      {
        total_trees: this.calculatedTrees,
      },
    );

    return result;
  });
}
